import { ICBLListData, ICBLstatsBodyprops, ICBLStatsData } from "@/api_framework/api_modals/CBL";
import { IClinicListResponseData } from "@/api_framework/api_modals/Clinics";
import { IOrgCityListResponseData, IOrgProviderListResponseData } from "@/api_framework/api_modals/Org";
import {
  IAtHomeBookingStatusSummaryData,
  IAtHomeBookingStatusSummaryParamsBody,
  IAtHomeListingFiltersParamsBody,
  IPatientWaitingListData,
  IPatientWaitingListFiltersParamsBody,
  IPatientWaitingListPagination,
  IPatientWaitingListGroomesData,
  IPatientWaitingStatsData,
  ITeleconsultationUserDoctorMeetDetailsResponseData,
} from "@/api_framework/api_modals/Patients";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IRouteReducerSliceStateInit } from "../slice_interface";

export type PatientWaitingListStateKeys =
  | "waitingList"
  | "waitingListPagination"
  | "waitingStats"
  | "atHomeWaitingStats"
  | "atHomeListingFilters"
  | "atHomeStatsFilters"
  | "atHomeWaitingStats"
  | "filters"
  | "groomers"
  | "clinics"
  | "cities"
  | "providers";

export interface IPatientWaitingListState {
  waitingList?: IPatientWaitingListData[] | null;
  waitingListPagination?: IPatientWaitingListPagination | null;
  waitingStats?: IPatientWaitingStatsData[] | null;
  atHomeWaitingStats?: IAtHomeBookingStatusSummaryData[] | null;
  atHomeListingFilters?: IAtHomeListingFiltersParamsBody | null;
  atHomeStatsFilters?: IAtHomeBookingStatusSummaryParamsBody | null;
  CBLList?: ICBLListData[] | null;
  CBLStats?: ICBLStatsData | null;
  filters: IPatientWaitingListFiltersParamsBody | null;
  groomers?: IPatientWaitingListGroomesData[] | null;
  clinics?: IClinicListResponseData[] | null;
  cities?: IOrgCityListResponseData[] | null;
  providers?: IOrgProviderListResponseData[] | null;
  CBLFilters?: ICBLstatsBodyprops | null;
  teleconsultationUserDoctorMeetDetails: ITeleconsultationUserDoctorMeetDetailsResponseData | null;
}

export interface IPatientWaitingListInitialState extends IRouteReducerSliceStateInit {
  state: IPatientWaitingListState;
}

const initialState: IPatientWaitingListInitialState = {
  state: {
    filters: null,
    waitingList: [],
    waitingListPagination: null,
    waitingStats: [],
    atHomeWaitingStats: [],
    atHomeListingFilters: null,
    atHomeStatsFilters: null,
    groomers: [],
    cities: [],
    clinics: [],
    providers: [],
    CBLStats: null,
    CBLList: [],
    CBLFilters: null,
    teleconsultationUserDoctorMeetDetails: null,
  },
};

const patientWaitingListSlice = createSlice({
  name: "patient_waiting_list_slice",
  initialState: initialState,
  reducers: {
    setOrgCities: (
      state,
      action: PayloadAction<{
        data: IOrgCityListResponseData[];
      }>,
    ) => {
      state.state.cities = action.payload.data;
    },
    setOrgProviders: (
      state,
      action: PayloadAction<{
        data: IOrgProviderListResponseData[];
      }>,
    ) => {
      state.state.providers = action.payload.data;
    },
    setOrgClinics: (
      state,
      action: PayloadAction<{
        data: IClinicListResponseData[];
      }>,
    ) => {
      state.state.clinics = action.payload.data;
    },
    setPWLGroomers: (
      state,
      action: PayloadAction<{
        data: IPatientWaitingListGroomesData[];
      }>,
    ) => {
      state.state.groomers = action.payload.data;
    },
    setPWLFilters: (
      state,
      action: PayloadAction<{
        filters: IPatientWaitingListFiltersParamsBody;
      }>,
    ) => {
      state.state.filters = action.payload.filters;
    },
    setPatientWaitingStats: (state, action: PayloadAction<{ data: IPatientWaitingStatsData[] }>) => {
      state.state.waitingStats = action.payload.data;
    },
    setAtHomePatientWaitingStats: (state, action: PayloadAction<{ data: IAtHomeBookingStatusSummaryData[] }>) => {
      state.state.atHomeWaitingStats = action.payload.data;
    },
    setAtHomeListingFilters: (
      state,
      action: PayloadAction<{
        filters: IAtHomeListingFiltersParamsBody;
      }>,
    ) => {
      state.state.atHomeListingFilters = action.payload.filters;
    },
    setAtHomeStatsFilters: (
      state,
      action: PayloadAction<{
        filters: IAtHomeBookingStatusSummaryParamsBody;
      }>,
    ) => {
      state.state.atHomeStatsFilters = action.payload.filters;
    },
    setPatientWaitingList: (
      state,
      action: PayloadAction<{
        data: IPatientWaitingListData[] | null;
        pagination?: IPatientWaitingListPagination | null;
      }>,
    ) => {
      state.state.waitingList = action.payload.data;
      state.state.waitingListPagination = action.payload.pagination ?? null;
    },
    setCBLFilters: (state, action: PayloadAction<{ data: ICBLstatsBodyprops }>) => {
      state.state.CBLFilters = action.payload.data;
    },
    setCBLList: (state, action: PayloadAction<{ data: ICBLListData[] }>) => {
      state.state.CBLList = action.payload.data;
    },
    setCBLStats: (state, action: PayloadAction<{ data: ICBLStatsData }>) => {
      state.state.CBLStats = action.payload.data;
    },

    setTeleconsultationUserDoctorMeetDetails: (
      state,
      action: PayloadAction<{ data: ITeleconsultationUserDoctorMeetDetailsResponseData }>,
    ) => {
      state.state.teleconsultationUserDoctorMeetDetails = action.payload.data;
    },
  },
});

export const {
  setOrgCities,
  setOrgClinics,
  setOrgProviders,
  setPWLFilters,
  setPWLGroomers,
  setPatientWaitingList,
  setPatientWaitingStats,
  setAtHomePatientWaitingStats,
  setAtHomeListingFilters,
  setAtHomeStatsFilters,
  setCBLFilters,
  setCBLStats,
  setCBLList,
  setTeleconsultationUserDoctorMeetDetails,
} = patientWaitingListSlice.actions;
export default patientWaitingListSlice.reducer;
